import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { CommentsResponse, GuestComment } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { Btn } from "../components/Btn";

const MAX_LEN = 500;

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function Guestbook({ isDesktop }: { isDesktop: boolean }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [comments, setComments] = useState<GuestComment[] | null>(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Anonymous visitors get the public feed (last initials only, like the
  // pledger wall); signed-in supporters get full names. The page must show real
  // content to anonymous crawlers — a bare sign-in wall here read as a phishing
  // funnel to Safe Browsing (July 2026 relisting).
  const load = () =>
    api
      .get<CommentsResponse>(user ? "/comments" : "/public/comments")
      .then((r) => setComments(r.comments))
      .catch(() => setComments([]));

  useEffect(() => {
    setComments(null);
    load();
  }, [user]);

  const submit = async () => {
    const trimmed = message.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/comments", { message: trimmed });
      setMessage("");
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const messageList =
    comments === null ? (
        <div style={{ padding: 20, color: "var(--ink-soft)", fontSize: 14 }}>Loading…</div>
      ) : comments.length === 0 ? (
        <div style={{ padding: 24, textAlign: "center", color: "var(--ink-soft)", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 16 }}>
          No messages yet — be the first to cheer Scott and Baker on.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(2, 1fr)" : "1fr", gap: 12 }}>
          {comments.map((c, i) => (
            <div key={i} style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 16, padding: 16 }}>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: "var(--ink)", whiteSpace: "pre-wrap" }}>
                {c.message}
              </p>
              <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--ink-soft)", fontWeight: 600 }}>
                {c.first} {c.last}
                <span style={{ fontWeight: 400 }}> · {timeAgo(c.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      );

  // Anonymous visitors see the messages plus an invitation to sign in to post.
  if (!user) {
    return (
      <div>
        <div
          style={{
            background: "var(--surface)",
            border: "1px solid var(--line)",
            borderRadius: 16,
            padding: isDesktop ? 20 : 16,
            marginBottom: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <span style={{ fontSize: 14, color: "var(--ink-soft)" }}>
            Want to add your own? Sign in to leave Scott and Baker words of encouragement.
          </span>
          <Btn size="md" variant="secondary" onClick={() => nav("/signin")}>
            Sign in
          </Btn>
        </div>
        {messageList}
      </div>
    );
  }

  return (
    <div>
      <div style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 16, padding: isDesktop ? 20 : 16, marginBottom: 18 }}>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX_LEN))}
          placeholder="Leave Scott and Baker words of encouragement…"
          rows={3}
          style={{
            width: "100%",
            padding: 12,
            fontFamily: "var(--ui)",
            fontSize: 15,
            border: "1px solid var(--line)",
            borderRadius: 10,
            background: "var(--bg)",
            color: "var(--ink)",
            resize: "vertical",
            boxSizing: "border-box",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 10 }}>
          <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>
            {error ? <span style={{ color: "var(--accent)" }}>{error}</span> : `${message.length}/${MAX_LEN}`}
          </span>
          <Btn size="md" variant="primary" onClick={submit} disabled={submitting || !message.trim()}>
            {submitting ? "Posting…" : "Sign the guest book"}
          </Btn>
        </div>
      </div>

      {messageList}
    </div>
  );
}
